import type { HttpErrorResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { firstValueFrom, map, switchMap, take } from 'rxjs';

import { AlertPosition } from '@valk-nx/components/ui-alert/src/lib/contracts/alert.types';
import { AlertService } from '@valk-nx/components/ui-alert/src/lib/services/alert.service';
import { BFF_URL, WITH_CREDENTIALS } from '@valk-nx/core/lib/core';
import type { CheckedOption } from '@valk-nx/core/lib/interfaces/form.interface';

export type Recipients = 'main' | 'booking' | 'business' | 'support';

export interface HotelRecipients {
	mails: { guid: string; type: string }[];
}

@Injectable({ providedIn: 'root' })
export class FormService {
	private readonly translate = inject(TranslateService);
	private readonly http = inject(HttpClient);
	private readonly alertService = inject(AlertService);
	private readonly recaptchaV3Service = inject(ReCaptchaV3Service);
	private readonly serviceUrl = inject(BFF_URL);
	private readonly withCredentials = inject<{
		withCredentials: boolean;
	}>(WITH_CREDENTIALS);

	submitCustomForm(
		subject: string,
		params: {
			id: string;
			label: string;
			value: string | CheckedOption[];
		}[],
		recipient: Recipients | undefined,
		hotelRecipients: HotelRecipients | undefined,
		hotelSlug: string,
		emailRecipients: string[],
		recaptchaToken?: string,
	): Promise<{
		success: boolean;
		status?: number;
	}> {
		const language = this.translate.getCurrentLang();
		const filteredParams = params.filter(
			(param) => typeof param.value !== 'boolean',
		);

		const hotelRecipientsGuids = hotelRecipients?.mails.map(
			(mail) => mail.guid,
		);
		// NOTE: if a token is provided through params, this means V2 of recaptcha is used
		if (recaptchaToken) {
			return this.recaptchaV2Submit(
				subject,
				filteredParams,
				recipient,
				hotelRecipientsGuids,
				hotelSlug,
				emailRecipients,
				language,
				recaptchaToken,
			);
		}
		return this.recaptchaV3Submit(
			subject,
			filteredParams,
			recipient,
			hotelRecipientsGuids,
			hotelSlug,
			emailRecipients,
			language,
		);
	}

	private recaptchaV3Submit(
		subject: string,
		params: {
			id: string;
			label: string;
			value: string | CheckedOption[];
		}[],
		recipient: Recipients,
		hotelRecipientsGuids: string[],
		hotelSlug: string,
		emailRecipients: string[],
		language: string,
	): Promise<{
		success: boolean;
		status?: number;
	}> {
		const recipients =
			hotelRecipientsGuids?.length > 0
				? { hotelRecipients: hotelRecipientsGuids }
				: { type: recipient };
		return firstValueFrom(
			this.recaptchaV3Service.execute('customForm').pipe(
				switchMap((token) => {
					return this.http.post<{ data: { success: true } }>(
						`${this.serviceUrl}form/submit`,
						{
							subject,
							params,
							token,
							...recipients,
							hotelSlug,
							emailRecipients,
							language,
							recaptchaVersion: 'v3',
						},
						{
							...this.withCredentials,
						},
					);
				}),
				take(1),
				map((body) => {
					return {
						success: body.data.success,
					};
				}),
			),
		).catch((err: HttpErrorResponse) => this.formError(err));
	}

	private recaptchaV2Submit(
		subject: string,
		params: {
			id: string;
			label: string;
			value: string | CheckedOption[];
		}[],
		recipient: Recipients,
		hotelRecipientsGuids: string[],
		hotelSlug: string,
		emailRecipients: string[],
		language: string,
		recaptchaToken: string,
	): Promise<{
		success: boolean;
		status?: number;
	}> {
		const recipients =
			hotelRecipientsGuids?.length > 0
				? { hotelRecipients: hotelRecipientsGuids }
				: { type: recipient };
		return firstValueFrom(
			this.http
				.post<{ data: { success: true } }>(
					`${this.serviceUrl}form/submit`,
					{
						subject,
						params,
						token: recaptchaToken,
						...recipients,
						hotelSlug,
						emailRecipients,
						language,
						recaptchaVersion: 'v2',
					},
					{
						...this.withCredentials,
					},
				)
				.pipe(
					take(1),
					map((body) => {
						return {
							success: body.data.success,
						};
					}),
				),
		).catch((err: HttpErrorResponse) => this.formError(err));
	}

	formError(err: HttpErrorResponse): never {
		this.alertService.error({
			closeable: true,
			duration: 5000,
			emphasized: false,
			hasElevation: true,
			position: AlertPosition.TopCenter,
			content: this.translate.instant('global.service.error'),
		});
		// Re-throw so callers receive the raw HttpErrorResponse and can decide
		// whether to escalate to reCAPTCHA v2 (see shouldUseRecaptchaV2).
		throw err;
	}
}
